import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  getStatusColor,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge, StatusDot } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Modal } from '@/components/ui/Modal';
import { useAppStore } from '@/store/appStore';
import type {
  Alert,
  AlertRule,
  AlertSeverity,
  AlertState,
  AlertEvent,
} from '@/types';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Clock,
  User,
  Search,
  SlidersHorizontal,
  Filter,
  Plus,
  RefreshCw,
  X,
  Send,
  History,
  MessageSquare,
  Check,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Settings,
  AlertOctagon,
  Info,
  Shield,
  Zap,
  Monitor,
  CheckSquare,
  ChevronRight,
  UserCheck,
  FileText,
  Activity,
} from 'lucide-react';

export function Alerts() {
  const navigate = useNavigate();

  // Active view tab: 'alerts' | 'rules'
  const [activeTab, setActiveTab] = useState<'alerts' | 'rules'>('alerts');

  // Alerts data & loading
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState<boolean>(true);

  // Filters for alerts
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected Alert Drawer states
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [noteText, setNoteText] = useState<string>('');
  const [actionProcessing, setActionProcessing] = useState<boolean>(false);
  const [assignedUser, setAssignedUser] = useState<string>('');

  // Alert Rules state
  const [rules, setAlertRules] = useState<AlertRule[]>([]);
  const [loadingRules, setLoadingRules] = useState<boolean>(true);
  const [ruleSearch, setRuleSearch] = useState<string>('');
  const [ruleSeverityFilter, setRuleSeverityFilter] = useState<string>('all');
  const [ruleModalOpen, setRuleModalOpen] = useState<boolean>(false);

  // Toast feedback
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const data = await mockApi.getAlerts({
        severity: severityFilter,
        state: stateFilter,
        search: searchQuery,
      });
      setAlerts(data);
    } catch (err) {
      console.error('Failed to load alerts', err);
    } finally {
      setLoadingAlerts(false);
    }
  }, [severityFilter, stateFilter, searchQuery]);

  // Fetch alert rules
  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const data = await mockApi.getAlertRules();
      setAlertRules(data);
    } catch (err) {
      console.error('Failed to load alert rules', err);
    } finally {
      setLoadingRules(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    if (activeTab === 'rules') {
      fetchRules();
    }
  }, [activeTab, fetchRules]);

  // Counts for KPI overview
  const activeAlertsCount = useMemo(() => {
    return alerts.filter((a) => a.state !== 'resolved').length;
  }, [alerts]);

  const criticalCount = useMemo(() => {
    return alerts.filter((a) => a.severity === 'critical' && a.state !== 'resolved').length;
  }, [alerts]);

  const warningCount = useMemo(() => {
    return alerts.filter((a) => (a.severity === 'warning' || a.severity === 'high') && a.state !== 'resolved').length;
  }, [alerts]);

  const acknowledgedCount = useMemo(() => {
    return alerts.filter((a) => a.state === 'acknowledged').length;
  }, [alerts]);

  const resolvedCount = useMemo(() => {
    return alerts.filter((a) => a.state === 'resolved').length;
  }, [alerts]);

  // Acknowledge single alert
  const handleAcknowledge = async (alertId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setActionProcessing(true);
    try {
      await mockApi.acknowledgeAlert(alertId);

      const now = new Date().toISOString();
      const newEvent: AlertEvent = {
        id: 'evt_' + Math.random().toString(36).substr(2, 9),
        timestamp: now,
        action: 'acknowledged',
        userId: 'u_admin',
        userName: 'Admin Technician',
        message: 'Alert acknowledged by operator',
      };

      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alertId
            ? {
                ...a,
                state: 'acknowledged',
                acknowledgedAt: now,
                acknowledgedBy: 'Admin Technician',
                history: [newEvent, ...(a.history || [])],
              }
            : a
        )
      );

      if (selectedAlert && selectedAlert.id === alertId) {
        setSelectedAlert((prev) =>
          prev
            ? {
                ...prev,
                state: 'acknowledged',
                acknowledgedAt: now,
                acknowledgedBy: 'Admin Technician',
                history: [newEvent, ...(prev.history || [])],
              }
            : null
        );
      }

      showToast('Alert acknowledged successfully');
    } catch (err) {
      console.error('Failed to acknowledge alert', err);
    } finally {
      setActionProcessing(false);
    }
  };

  // Resolve single alert
  const handleResolve = async (alertId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setActionProcessing(true);
    try {
      await mockApi.resolveAlert(alertId);

      const now = new Date().toISOString();
      const newEvent: AlertEvent = {
        id: 'evt_' + Math.random().toString(36).substr(2, 9),
        timestamp: now,
        action: 'resolved',
        userId: 'u_admin',
        userName: 'Admin Technician',
        message: 'Alert state set to resolved',
      };

      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alertId
            ? {
                ...a,
                state: 'resolved',
                history: [newEvent, ...(a.history || [])],
              }
            : a
        )
      );

      if (selectedAlert && selectedAlert.id === alertId) {
        setSelectedAlert((prev) =>
          prev
            ? {
                ...prev,
                state: 'resolved',
                history: [newEvent, ...(prev.history || [])],
              }
            : null
        );
      }

      showToast('Alert marked as resolved');
    } catch (err) {
      console.error('Failed to resolve alert', err);
    } finally {
      setActionProcessing(false);
    }
  };

  // Add Note to alert
  const handleAddNote = () => {
    if (!selectedAlert || !noteText.trim()) return;

    const now = new Date().toISOString();
    const newNoteText = noteText.trim();

    const newEvent: AlertEvent = {
      id: 'evt_' + Math.random().toString(36).substr(2, 9),
      timestamp: now,
      action: 'note_added',
      userId: 'u_admin',
      userName: 'Admin Technician',
      message: `Note added: "${newNoteText}"`,
    };

    const updatedNotes = [...(selectedAlert.notes || []), newNoteText];
    const updatedHistory = [newEvent, ...(selectedAlert.history || [])];

    const updatedAlert: Alert = {
      ...selectedAlert,
      notes: updatedNotes,
      history: updatedHistory,
    };

    setSelectedAlert(updatedAlert);
    setAlerts((prev) => prev.map((a) => (a.id === selectedAlert.id ? updatedAlert : a)));
    setNoteText('');
    showToast('Note added to alert');
  };

  // Assign alert
  const handleAssignUser = () => {
    if (!selectedAlert || !assignedUser.trim()) return;

    const now = new Date().toISOString();
    const newEvent: AlertEvent = {
      id: 'evt_' + Math.random().toString(36).substr(2, 9),
      timestamp: now,
      action: 'assigned',
      userId: 'u_admin',
      userName: 'Admin Technician',
      message: `Assigned alert to ${assignedUser}`,
    };

    const updatedAlert: Alert = {
      ...selectedAlert,
      assignedTo: assignedUser,
      state: selectedAlert.state === 'open' ? 'assigned' : selectedAlert.state,
      history: [newEvent, ...(selectedAlert.history || [])],
    };

    setSelectedAlert(updatedAlert);
    setAlerts((prev) => prev.map((a) => (a.id === selectedAlert.id ? updatedAlert : a)));
    showToast(`Alert assigned to ${assignedUser}`);
  };

  // Toggle Alert Rule enabled
  const handleToggleRule = async (ruleId: string) => {
    try {
      await mockApi.toggleAlertRule(ruleId, true);
      setAlertRules((prev) =>
        prev.map((r) => (r.id === ruleId ? { ...r, enabled: !r.enabled } : r))
      );
      showToast('Alert rule status updated');
    } catch (err) {
      console.error('Failed to toggle rule', err);
    }
  };

  // Filtered Alert Rules
  const filteredRules = useMemo(() => {
    return rules.filter((r) => {
      const matchesSearch =
        !ruleSearch ||
        r.name.toLowerCase().includes(ruleSearch.toLowerCase()) ||
        r.type.toLowerCase().includes(ruleSearch.toLowerCase()) ||
        r.targetValue.toLowerCase().includes(ruleSearch.toLowerCase());

      const matchesSeverity =
        ruleSeverityFilter === 'all' || r.severity === ruleSeverityFilter;

      return matchesSearch && matchesSeverity;
    });
  }, [rules, ruleSearch, ruleSeverityFilter]);

  // Open Alert Drawer
  const handleOpenDrawer = (alert: Alert) => {
    setSelectedAlert(alert);
    setAssignedUser(alert.assignedTo || '');
    setDrawerOpen(true);
  };

  // Render Severity Icon
  const renderSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="h-4 w-4 text-[var(--status-critical)] shrink-0" />;
      case 'warning':
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-[var(--status-warning)] shrink-0" />;
      case 'medium':
        return <Info className="h-4 w-4 text-amber-400 shrink-0" />;
      case 'low':
      default:
        return <Info className="h-4 w-4 text-blue-400 shrink-0" />;
    }
  };

  // Render State Badge
  const renderStateBadge = (state: AlertState) => {
    switch (state) {
      case 'open':
        return <Badge variant="critical">Open</Badge>;
      case 'acknowledged':
        return <Badge variant="warning">Acknowledged</Badge>;
      case 'assigned':
        return <Badge variant="info">Assigned</Badge>;
      case 'resolved':
        return <Badge variant="online">Resolved</Badge>;
      case 'escalated':
        return <Badge variant="critical">Escalated</Badge>;
      default:
        return <Badge variant="default">{state}</Badge>;
    }
  };

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-[1700px] mx-auto min-h-screen">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[200] bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-primary)] px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2.5 text-xs animate-in">
          <CheckCircle2 className="h-4 w-4 text-[var(--status-online)]" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              Alerts & Monitoring
            </h1>
            <Badge variant="critical" className="font-mono text-xs px-2 py-0.5">
              {activeAlertsCount} active
            </Badge>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Real-time metric thresholds, hardware diagnostic alerts, and notification rules
          </p>
        </div>

        {/* View Mode Tab Switcher */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center bg-[var(--panel)] border border-[var(--border)] rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('alerts')}
              className={cn(
                'px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-2 transition-colors',
                activeTab === 'alerts'
                  ? 'bg-[var(--hover)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              )}
            >
              <Bell className="h-3.5 w-3.5 text-amber-400" />
              <span>Active Alerts</span>
              {activeAlertsCount > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full bg-[var(--status-critical)]/20 text-[var(--status-critical)] text-2xs font-mono font-bold">
                  {activeAlertsCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('rules')}
              className={cn(
                'px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-2 transition-colors',
                activeTab === 'rules'
                  ? 'bg-[var(--hover)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              )}
            >
              <Shield className="h-3.5 w-3.5 text-blue-400" />
              <span>Alert Rules</span>
            </button>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => (activeTab === 'alerts' ? fetchAlerts() : fetchRules())}
            disabled={loadingAlerts || loadingRules}
            className="gap-1.5"
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                (loadingAlerts || loadingRules) && 'animate-spin'
              )}
            />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Tab 1: Active Alerts View */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {/* KPI Summary Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="p-3.5 surface flex items-center justify-between border-[var(--border)]">
              <div className="flex flex-col">
                <span className="text-2xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  Active Alerts
                </span>
                <span className="text-xl font-bold font-mono text-[var(--text-primary)] mt-0.5">
                  {activeAlertsCount}
                </span>
              </div>
              <div className="h-9 w-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <Bell className="h-5 w-5" />
              </div>
            </Card>

            <Card className="p-3.5 surface flex items-center justify-between border-[var(--border)]">
              <div className="flex flex-col">
                <span className="text-2xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  Critical
                </span>
                <span className="text-xl font-bold font-mono text-[var(--status-critical)] mt-0.5">
                  {criticalCount}
                </span>
              </div>
              <div className="h-9 w-9 rounded-lg bg-[var(--status-critical)]/10 border border-[var(--status-critical)]/20 flex items-center justify-center text-[var(--status-critical)]">
                <AlertOctagon className="h-5 w-5" />
              </div>
            </Card>

            <Card className="p-3.5 surface flex items-center justify-between border-[var(--border)]">
              <div className="flex flex-col">
                <span className="text-2xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  Acknowledged
                </span>
                <span className="text-xl font-bold font-mono text-[var(--status-warning)] mt-0.5">
                  {acknowledgedCount}
                </span>
              </div>
              <div className="h-9 w-9 rounded-lg bg-[var(--status-warning)]/10 border border-[var(--status-warning)]/20 flex items-center justify-center text-[var(--status-warning)]">
                <UserCheck className="h-5 w-5" />
              </div>
            </Card>

            <Card className="p-3.5 surface flex items-center justify-between border-[var(--border)]">
              <div className="flex flex-col">
                <span className="text-2xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  Resolved
                </span>
                <span className="text-xl font-bold font-mono text-[var(--status-online)] mt-0.5">
                  {resolvedCount}
                </span>
              </div>
              <div className="h-9 w-9 rounded-lg bg-[var(--status-online)]/10 border border-[var(--status-online)]/20 flex items-center justify-center text-[var(--status-online)]">
                <CheckCircle2 className="h-5 w-5" />
              </div>
            </Card>
          </div>

          {/* Filter Bar */}
          <Card className="p-3.5 bg-[var(--panel)] border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto flex-1">
              {/* Search */}
              <div className="relative min-w-[220px] flex-1 sm:flex-initial">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-muted)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search host, rule, message..."
                  className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md pl-8 pr-7 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Severity Filter */}
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
              >
                <option value="all">Severity: All</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              {/* State Filter */}
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
              >
                <option value="all">State: All</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="assigned">Assigned</option>
                <option value="escalated">Escalated</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {(searchQuery || severityFilter !== 'all' || stateFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSeverityFilter('all');
                  setStateFilter('all');
                }}
                className="text-xs text-[var(--accent)] hover:underline flex items-center gap-1 shrink-0"
              >
                <X className="h-3.5 w-3.5" />
                Reset filters
              </button>
            )}
          </Card>

          {/* Alert Cards Grid / List */}
          <div className="space-y-2.5">
            {loadingAlerts ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <Card
                  key={idx}
                  className="p-4 surface border-[var(--border)] rounded-lg animate-pulse space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="h-4 w-32 bg-[var(--hover)] rounded" />
                    <div className="h-4 w-20 bg-[var(--hover)] rounded" />
                  </div>
                  <div className="h-4 w-3/4 bg-[var(--hover)] rounded" />
                  <div className="h-3 w-1/2 bg-[var(--hover)] rounded" />
                </Card>
              ))
            ) : alerts.length === 0 ? (
              <Card className="p-12 text-center surface border-[var(--border)] rounded-lg">
                <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                  <div className="h-10 w-10 rounded-full bg-[var(--hover)] flex items-center justify-center text-[var(--text-muted)]">
                    <Bell className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-semibold text-[var(--text-secondary)]">
                    No alerts match criteria
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    All system metric thresholds are healthy or no alerts match your filter configuration.
                  </p>
                  {(searchQuery || severityFilter !== 'all' || stateFilter !== 'all') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setSeverityFilter('all');
                        setStateFilter('all');
                      }}
                      className="mt-2"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              alerts.map((alert) => {
                const borderAccent =
                  alert.severity === 'critical'
                    ? 'border-l-4 border-l-[var(--status-critical)]'
                    : alert.severity === 'warning' || alert.severity === 'high'
                    ? 'border-l-4 border-l-[var(--status-warning)]'
                    : 'border-l-4 border-l-blue-500';

                return (
                  <Card
                    key={alert.id}
                    onClick={() => handleOpenDrawer(alert)}
                    className={cn(
                      'p-4 surface border-[var(--border)] rounded-lg hover:border-[var(--border-strong)] transition-all cursor-pointer group flex flex-col md:flex-row md:items-center justify-between gap-4',
                      borderAccent
                    )}
                  >
                    {/* Main Content */}
                    <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {renderSeverityIcon(alert.severity)}
                        <Badge variant={alert.severity} className="uppercase font-mono text-2xs py-0">
                          {alert.severity}
                        </Badge>

                        {renderStateBadge(alert.state)}

                        <span className="text-[var(--text-muted)] font-mono text-2xs">
                          {timeAgo(alert.createdAt)}
                        </span>

                        <span className="text-[var(--text-muted)] text-2xs">•</span>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/devices/${alert.deviceId}`);
                          }}
                          className="font-mono text-xs font-bold text-[var(--text-primary)] hover:text-[var(--accent)] hover:underline flex items-center gap-1"
                        >
                          <Monitor className="h-3 w-3 text-[var(--text-muted)]" />
                          <span>{alert.hostname}</span>
                        </button>

                        <span className="text-[var(--text-muted)] text-2xs">
                          ({alert.client} / {alert.site})
                        </span>
                      </div>

                      <h3 className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors">
                        {alert.ruleName}
                      </h3>

                      <p className="text-xs text-[var(--text-secondary)] line-clamp-1">
                        {alert.message}
                      </p>

                      {alert.assignedTo && (
                        <div className="flex items-center gap-1.5 text-2xs text-[var(--text-muted)] mt-0.5">
                          <User className="h-3 w-3" />
                          <span>Assigned to: <strong className="text-[var(--text-secondary)]">{alert.assignedTo}</strong></span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 border-t md:border-t-0 border-[var(--border-subtle)] pt-2 md:pt-0">
                      {alert.state === 'open' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => handleAcknowledge(alert.id, e)}
                          loading={actionProcessing}
                          className="h-8 text-xs gap-1"
                        >
                          <UserCheck className="h-3.5 w-3.5 text-amber-400" />
                          <span>Acknowledge</span>
                        </Button>
                      )}

                      {alert.state !== 'resolved' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => handleResolve(alert.id, e)}
                          loading={actionProcessing}
                          className="h-8 text-xs gap-1 hover:text-[var(--status-online)]"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Resolve</span>
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDrawer(alert);
                        }}
                        className="h-8 text-xs gap-1"
                      >
                        <span>Details</span>
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Alert Rules View */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          <Card className="p-3.5 bg-[var(--panel)] border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto flex-1">
              {/* Search */}
              <div className="relative min-w-[240px] flex-1 sm:flex-initial">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-muted)]" />
                <input
                  type="text"
                  value={ruleSearch}
                  onChange={(e) => setRuleSearch(e.target.value)}
                  placeholder="Filter alert rules..."
                  className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md pl-8 pr-7 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
                />
                {ruleSearch && (
                  <button
                    onClick={() => setRuleSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Severity Filter */}
              <select
                value={ruleSeverityFilter}
                onChange={(e) => setRuleSeverityFilter(e.target.value)}
                className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
              >
                <option value="all">Severity: All</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={() => setRuleModalOpen(true)}
              className="gap-1.5 shrink-0"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Create Alert Rule</span>
            </Button>
          </Card>

          {/* Rules Table */}
          <Card className="surface overflow-hidden border-[var(--border)] rounded-lg">
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left border-collapse min-w-[900px]">
                <thead>
                  <tr className="bg-[var(--elevated)] border-b border-[var(--border)] text-2xs uppercase tracking-wider text-[var(--text-muted)] font-medium">
                    <th className="py-2.5 px-4 w-16 text-center">Status</th>
                    <th className="py-2.5 px-4">Rule Name</th>
                    <th className="py-2.5 px-4">Type</th>
                    <th className="py-2.5 px-4">Severity</th>
                    <th className="py-2.5 px-4">Target Scope</th>
                    <th className="py-2.5 px-4">Cooldown</th>
                    <th className="py-2.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-[var(--border-subtle)] text-xs">
                  {loadingRules ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="py-3 px-4">
                          <div className="h-4 w-8 bg-[var(--hover)] rounded mx-auto" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-36 bg-[var(--hover)] rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-24 bg-[var(--hover)] rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-16 bg-[var(--hover)] rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-24 bg-[var(--hover)] rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-12 bg-[var(--hover)] rounded" />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="h-4 w-12 bg-[var(--hover)] rounded ml-auto" />
                        </td>
                      </tr>
                    ))
                  ) : filteredRules.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-[var(--text-muted)]">
                        No alert rules match the filter criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-[var(--hover)] transition-colors">
                        {/* Status Switch */}
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleToggleRule(rule.id)}
                            className="text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors"
                            title={rule.enabled ? 'Click to disable rule' : 'Click to enable rule'}
                          >
                            {rule.enabled ? (
                              <ToggleRight className="h-6 w-6 text-emerald-400" />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-[var(--text-muted)]" />
                            )}
                          </button>
                        </td>

                        {/* Name */}
                        <td className="py-3 px-4 font-semibold text-[var(--text-primary)]">
                          {rule.name}
                        </td>

                        {/* Type */}
                        <td className="py-3 px-4 font-mono text-2xs text-[var(--text-secondary)]">
                          {rule.type}
                        </td>

                        {/* Severity */}
                        <td className="py-3 px-4">
                          <Badge variant={rule.severity} className="uppercase font-mono text-2xs">
                            {rule.severity}
                          </Badge>
                        </td>

                        {/* Target Scope */}
                        <td className="py-3 px-4 text-[var(--text-secondary)]">
                          <span className="capitalize font-medium">{rule.target}</span>
                          {rule.targetValue && rule.targetValue !== 'all' && (
                            <span className="text-[var(--text-muted)] ml-1 font-mono text-2xs">
                              ({rule.targetValue})
                            </span>
                          )}
                        </td>

                        {/* Cooldown */}
                        <td className="py-3 px-4 font-mono text-2xs text-[var(--text-muted)]">
                          {rule.cooldownMin} min
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleRule(rule.id)}
                              className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            >
                              {rule.enabled ? 'Disable' : 'Enable'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Alert Details Slide-out Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedAlert ? `Alert: ${selectedAlert.ruleName}` : 'Alert Details'}
        width={560}
      >
        {selectedAlert && (
          <div className="p-5 space-y-5 text-xs">
            {/* Header info strip */}
            <div className="flex items-center justify-between gap-2 p-3 surface border-[var(--border)] rounded-lg">
              <div className="flex items-center gap-2">
                {renderSeverityIcon(selectedAlert.severity)}
                <Badge variant={selectedAlert.severity} className="uppercase font-mono text-2xs">
                  {selectedAlert.severity}
                </Badge>
                {renderStateBadge(selectedAlert.state)}
              </div>
              <span className="font-mono text-2xs text-[var(--text-muted)]">
                {timeAgo(selectedAlert.createdAt)}
              </span>
            </div>

            {/* Target Device & Scope */}
            <div className="surface p-3 space-y-2 border-[var(--border)] rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-muted)]">Device Hostname:</span>
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate(`/devices/${selectedAlert.deviceId}`);
                  }}
                  className="font-mono font-bold text-[var(--accent)] hover:underline flex items-center gap-1"
                >
                  <span>{selectedAlert.hostname}</span>
                  <ExternalLink className="h-3 w-3" />
                </button>
              </div>

              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Client / Site:</span>
                <span className="text-[var(--text-primary)] font-medium">
                  {selectedAlert.client} / {selectedAlert.site}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Created At:</span>
                <span className="font-mono text-[var(--text-secondary)]">
                  {formatDate(selectedAlert.createdAt)}
                </span>
              </div>

              {selectedAlert.acknowledgedAt && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Acknowledged By:</span>
                  <span className="text-[var(--status-warning)] font-medium">
                    {selectedAlert.acknowledgedBy} at {formatDate(selectedAlert.acknowledgedAt)}
                  </span>
                </div>
              )}
            </div>

            {/* Full Message & Details */}
            <div className="space-y-1.5">
              <label className="font-semibold text-[var(--text-secondary)] uppercase text-2xs tracking-wider">
                Diagnostic Output
              </label>
              <div className="surface p-3 border-[var(--border)] rounded-lg space-y-2">
                <p className="font-semibold text-[var(--text-primary)]">{selectedAlert.message}</p>
                {selectedAlert.details && (
                  <pre className="bg-[var(--bg)] p-2.5 rounded border border-[var(--border)] text-2xs font-mono text-[var(--text-secondary)] whitespace-pre-wrap break-all overflow-x-auto">
                    {selectedAlert.details}
                  </pre>
                )}
              </div>
            </div>

            {/* Primary Action Buttons */}
            <div className="space-y-2 pt-1 border-t border-[var(--border-subtle)]">
              <label className="font-semibold text-[var(--text-secondary)] uppercase text-2xs tracking-wider">
                Incident Response Actions
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  onClick={() => handleAcknowledge(selectedAlert.id)}
                  disabled={selectedAlert.state === 'acknowledged' || selectedAlert.state === 'resolved'}
                  loading={actionProcessing}
                  className="gap-1.5"
                >
                  <UserCheck className="h-3.5 w-3.5 text-amber-400" />
                  <span>Acknowledge</span>
                </Button>

                <Button
                  variant="primary"
                  onClick={() => handleResolve(selectedAlert.id)}
                  disabled={selectedAlert.state === 'resolved'}
                  loading={actionProcessing}
                  className="gap-1.5"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Resolve Alert</span>
                </Button>
              </div>

              {/* Assignee Box */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="text"
                  value={assignedUser}
                  onChange={(e) => setAssignedUser(e.target.value)}
                  placeholder="Assign technician name..."
                  className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-1.5 text-xs text-[var(--text-primary)] flex-1 focus:outline-none focus:border-[var(--accent)]"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAssignUser}
                  disabled={!assignedUser.trim()}
                  className="shrink-0"
                >
                  Assign
                </Button>
              </div>
            </div>

            {/* Notes Section */}
            <div className="space-y-2 pt-2 border-t border-[var(--border-subtle)]">
              <div className="flex items-center justify-between">
                <label className="font-semibold text-[var(--text-secondary)] uppercase text-2xs tracking-wider flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-[var(--accent)]" />
                  <span>Technician Notes ({selectedAlert.notes?.length || 0})</span>
                </label>
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {(!selectedAlert.notes || selectedAlert.notes.length === 0) ? (
                  <p className="text-2xs text-[var(--text-muted)] italic">No notes added yet.</p>
                ) : (
                  selectedAlert.notes.map((note, idx) => (
                    <div key={idx} className="surface p-2.5 border-[var(--border)] rounded-md text-2xs text-[var(--text-primary)]">
                      {note}
                    </div>
                  ))
                )}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a note..."
                  onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                  className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-1.5 text-xs text-[var(--text-primary)] flex-1 focus:outline-none focus:border-[var(--accent)]"
                />
                <Button variant="secondary" size="sm" onClick={handleAddNote} disabled={!noteText.trim()}>
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* History Timeline */}
            <div className="space-y-2 pt-2 border-t border-[var(--border-subtle)]">
              <label className="font-semibold text-[var(--text-secondary)] uppercase text-2xs tracking-wider flex items-center gap-1.5">
                <History className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                <span>Incident Timeline</span>
              </label>

              <div className="relative border-l-2 border-[var(--border)] ml-2 pl-4 space-y-3">
                {(!selectedAlert.history || selectedAlert.history.length === 0) ? (
                  <p className="text-2xs text-[var(--text-muted)]">No recorded events.</p>
                ) : (
                  selectedAlert.history.map((evt) => (
                    <div key={evt.id} className="relative text-2xs">
                      <div className="absolute -left-[21px] top-0.5 h-2.5 w-2.5 rounded-full bg-[var(--accent)] border-2 border-[var(--panel)]" />
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[var(--text-primary)] capitalize">
                          {evt.action.replace('_', ' ')}
                        </span>
                        <span className="text-[var(--text-muted)] font-mono">
                          {timeAgo(evt.timestamp)}
                        </span>
                      </div>
                      <p className="text-[var(--text-secondary)] mt-0.5">{evt.message}</p>
                      <p className="text-[var(--text-muted)] mt-0.2">By: {evt.userName}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Modal for Creating / Editing Rule */}
      <Modal
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        title="Create New Alert Threshold Rule"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setRuleModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setRuleModalOpen(false);
                showToast('Alert rule created');
              }}
            >
              Create Rule
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-xs">
          <div>
            <label className="block font-medium text-[var(--text-secondary)] mb-1">
              Rule Name
            </label>
            <input
              type="text"
              placeholder="e.g. Memory Usage Exceeds 92%"
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-[var(--text-secondary)] mb-1">
                Metric / Event Type
              </label>
              <select className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]">
                <option value="high_cpu">High CPU Usage</option>
                <option value="high_memory">High Memory Usage</option>
                <option value="low_disk">Low Disk Space</option>
                <option value="device_offline">Device Offline</option>
                <option value="antivirus_inactive">Antivirus Inactive</option>
              </select>
            </div>

            <div>
              <label className="block font-medium text-[var(--text-secondary)] mb-1">
                Severity
              </label>
              <select className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]">
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-[var(--text-secondary)] mb-1">
                Target Scope
              </label>
              <select className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]">
                <option value="all">All Managed Devices</option>
                <option value="client">Specific Client</option>
                <option value="site">Specific Site</option>
                <option value="tag">Specific Tag</option>
              </select>
            </div>

            <div>
              <label className="block font-medium text-[var(--text-secondary)] mb-1">
                Cooldown (Minutes)
              </label>
              <input
                type="number"
                defaultValue={15}
                className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
